import {Fragment, useState, useMemo, useCallback} from 'react'
import {useParams, useNavigate, useSearchParams, useLocation} from 'react-router-dom'
import {useQuery} from '@tanstack/react-query'
import {AppInstant, formatTimestamp} from '@/lib/date-utils'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useDialog} from '@/hooks/useDialog'
import {ChevronLeft, ChevronRight, AlertTriangle, ShieldCheck} from 'lucide-react'
import {usePageTitle} from '@/hooks/use-page-title'
import {useServerPagination} from '@/hooks/use-server-pagination'
import {CertificationReviewControllerDecisionsEndpoint} from '@/types/endpoint-contracts'
import {useAuth} from '@/contexts/auth-context'
import {Button} from '@/components/ui/button'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {Card, CardHeader, CardTitle, CardContent} from '@/components/ui/card'
import {Textarea} from '@/components/ui/textarea'
import {CONCEPT_ICON} from '@/lib/concepts'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {Pagination} from '@/components/ui/pagination'
import {ErrorBanner} from '@/components/ui/error-banner'
import {RiskLevelBadge} from '@/components/ui/risk-level-badge'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {Strong} from '@/components/shared/Strong'
import {useToast} from '@/hooks/use-toast'
import {routes} from '@/lib/routes'
import {CertificationDecisionType, ReviewItemStatus} from '@/types/certification'
import type {CertificationDecision} from '@/types/certification'
import {PageLayout} from '@/components/shared/PageLayout'
import {InlineRow} from '@/components/shared/InlineRow'
import {FormField} from '@/components/shared/FormField'
import {FormGrid} from '@/components/shared/FormGrid'
import {MutedText} from '@/components/shared/MutedText'
import {Icon} from '@/components/shared/Icon'
import {SegmentedToggle, type SegmentedToggleOption} from '@/components/shared/SegmentedToggle'

function DecisionToggle({
    decision,
    onDecide,
    isPending,
    readOnly,
    i18n,
}: {
    decision: CertificationDecision
    onDecide: (assignmentId: string, decisionType: CertificationDecisionType) => void
    isPending: boolean
    readOnly?: boolean
    i18n: ReturnType<typeof useI18n>
}) {
    const isAutoRevoke = decision.decisionType === CertificationDecisionType.AUTO_REVOKE
    const current = decision.decisionType ?? CertificationDecisionType.UNDECIDED

    if (isAutoRevoke) {
        return (
            <MutedText flex size="sm">
                <Icon icon={AlertTriangle}/>
                {decision.autoRevokeReason}
            </MutedText>
        )
    }

    /* eslint-disable i18next/no-literal-string -- technical test-id identifiers and tone keys, not user-visible strings */
    const options: ReadonlyArray<SegmentedToggleOption<CertificationDecisionType>> = [
        {
            value: CertificationDecisionType.KEEP,
            label: i18n.certificationReviews.keep,
            tone: 'success',
            testId: `btn-keep-${decision.id}`,
        },
        {
            value: CertificationDecisionType.REVOKE,
            label: i18n.certificationReviews.decisionRevoke,
            tone: 'destructive',
            testId: `btn-revoke-${decision.id}`,
        },
        {
            value: CertificationDecisionType.UNDECIDED,
            label: i18n.certificationReviews.undecided,
            tone: 'neutral',
            testId: `btn-undecided-${decision.id}`,
        },
    ]
    /* eslint-enable i18next/no-literal-string */

    return (
        <SegmentedToggle
            options={options}
            value={current}
            onChange={(v) => onDecide(decision.assignmentId!, v)}
            disabled={readOnly || isPending || !decision.assignmentId}
            data-testid={`decision-toggle-${decision.id}`}
        />
    )
}
/** Group consecutive decisions by risk level for section headers */
function groupByRiskLevel(decisions: CertificationDecision[]) {
    const groups: {riskLevel: string | null; decisions: CertificationDecision[]}[] = []
    let currentGroup: {riskLevel: string | null; decisions: CertificationDecision[]} | null = null

    for (const d of decisions) {
        const rl = d.riskLevel ?? null
        if (!currentGroup || currentGroup.riskLevel !== rl) {
            currentGroup = {riskLevel: rl, decisions: []}
            groups.push(currentGroup)
        }
        currentGroup.decisions.push(d)
    }

    return groups
}

export function AssignmentDecisions() {
    const services = useServices()
    const {itemId} = useParams<{itemId: string}>()
    const navigate = useNavigate()
    const location = useLocation()
    const {toast} = useToast()
    const i18n = useI18n()
    const {user} = useAuth()
    const [searchParams] = useSearchParams()
    const readOnly = searchParams.get('readonly') === 'true'
    const locationState = location.state as {from?: string; campaignId?: string} | null

    // Fetch the review item to get the identity name for the page title
    const {data: reviewItem} = useQuery({
        queryKey: ['certification-review-item', itemId],
        queryFn: () => services.certificationReviews.detail(itemId!),
        enabled: !!itemId,
    })
    const identityName = reviewItem?.identity.displayName
    const campaignId = reviewItem?.campaignId
    usePageTitle(identityName ?? i18n.certificationReviews.allAssignments)

    // Fetch prev/next navigation within the campaign
    const isCampaignMode = locationState?.from === 'campaign'
    const {data: navigation} = useQuery({
        queryKey: ['certification-review-navigation', itemId, campaignId, isCampaignMode],
         
        queryFn: () => services.certification.reviewNavigation(itemId!, {campaignId: campaignId!, ...(isCampaignMode ? {mode: 'campaign'} : {})}),
        enabled: !!itemId && !!campaignId,
    })

    const navigateToItem = useCallback((targetItemId: string) => {
        // Navigate from /:itemId/assignments to /:targetItemId/assignments
        const readOnlyParam = readOnly ? '?readonly=true' : '' // eslint-disable-line i18next/no-literal-string -- URL parameter
        // eslint-disable-next-line i18next/no-literal-string
        navigate(`../../${targetItemId}/assignments${readOnlyParam}`, {
            relative: 'path',
            state: locationState,
        })
    }, [navigate, readOnly, locationState])

    /* eslint-disable i18next/no-literal-string -- audit trail comments are always English */
    const buildDefaultComment = useCallback((params: {
        decisionType: CertificationDecisionType
        accessProfileName?: string | null
        riskLevel?: string | null
        scope?: 'single' | 'bulk'
        filterInfo?: string
    }) => {
        const timestamp = formatTimestamp(AppInstant.now())
        const reviewer = user ? `${user.name} (${user.email})` : 'Unknown'
        const roles = user?.roles?.join(', ') ?? 'N/A'
        const scope = params.scope === 'bulk' ? 'bulk decision' : 'individual decision'
        const parts = [
            `${params.decisionType} ${scope} by ${reviewer} [${roles}] on ${timestamp}`,
        ]
        if (params.accessProfileName) parts.push(`for access profile "${params.accessProfileName}"`)
        if (params.riskLevel) parts.push(`with risk level ${params.riskLevel}`)
        if (params.filterInfo) parts.push(`(filters: ${params.filterInfo})`)
        return parts.join(' ') + '.'
    }, [user])
    /* eslint-enable i18next/no-literal-string */

    // CertificationReviewController.getDecisions declares @SortableFields({}) — the
    // backend applies a fixed risk-level + localized-name ordering. An empty
    // defaultSortBy suppresses the sort= URL parameter so the runtime validator
    // accepts the request.
    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: CertificationReviewControllerDecisionsEndpoint,
        defaultSortBy: '',
        sortable: false,
    })

    const riskLevelFilter = (searchAndFilters.filters.RISK_LEVEL ?? '') as string
    const decisionTypeFilter = (searchAndFilters.filters.DECISION_TYPE ?? '') as string

    // Paginated decisions query
    const {data: decisionsPage, isLoading, error} = useQuery({
        queryKey: ['certification-decisions', itemId, queryString],
        queryFn: () => services.certification.listDecisions(itemId!, Object.fromEntries(new URLSearchParams(queryString))),
        enabled: !!itemId,
    })

    const decisions = decisionsPage?.content ?? []
    const totalItems = decisionsPage?.totalElements ?? 0
    const totalPages = decisionsPage?.totalPages ?? 0
    const hasActiveFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
    }, [searchAndFilters])

    const allDecided = reviewItem
        ? reviewItem.decidedAssignments === reviewItem.totalAssignments && reviewItem.totalAssignments > 0
        : false

    // Certify mutation
    const certifyDialog = useDialog()
    const certifyMutation = useEntityMutation({
        mutationFn: () => services.certification.certify(itemId!, {}),
        invalidate: [
            ['certification-decisions', itemId],
            ['certification-review-item', itemId],
            ['certification-my-reviews'],
            ['certification-my-campaigns'],
            ['certification-review-navigation'],
        ],
        successMessage: i18n.certificationReviews.certifySuccess({name: identityName ?? ''}),
        closeDialog: certifyDialog.close,
        onSuccess: () => {
            if (navigation?.next) {
                navigateToItem(navigation.next.id)
            } else if (navigation?.previous) {
                navigateToItem(navigation.previous.id)
            } else {
                goBack()
            }
        },
    })

    // Single decide mutation
    const decideMutation = useEntityMutation({
        mutationFn: (params: {assignmentId: string; decisionType: CertificationDecisionType; comment: string}) =>
            services.certification.decide(itemId!, params),
        invalidate: [
            ['certification-decisions', itemId],
            ['certification-review-item', itemId],
            ['certification-my-reviews'],
        ],
        onSuccess: (_data, variables) => {
            const msg = variables.decisionType === CertificationDecisionType.KEEP
                ? i18n.certificationReviews.decisionKept
                : variables.decisionType === CertificationDecisionType.REVOKE
                    ? i18n.certificationReviews.decisionRevoked
                    : i18n.certificationReviews.decisionReset
            toast({description: msg})
        },
    })

    // Bulk decide mutation
    const bulkDialog = useDialog()
    const [bulkDecisionType, setBulkDecisionType] = useState<CertificationDecisionType>(CertificationDecisionType.KEEP)
    const [bulkComment, setBulkComment] = useState('')

    const bulkDecideMutation = useEntityMutation({
        mutationFn: () =>
            services.certification.bulkDecide(itemId!, {
                decisionType: bulkDecisionType,
                comment: bulkComment || null,
                riskLevel: riskLevelFilter || null,
                currentDecisionType: decisionTypeFilter || null,
            }),
        invalidate: [
            ['certification-decisions', itemId],
            ['certification-review-item', itemId],
            ['certification-my-reviews'],
        ],
        closeDialog: bulkDialog.close,
        onSuccess: (data) => {
            toast({description: i18n.certificationReviews.bulkDecideSuccess({count: (data as {affected: number}).affected})})
            setBulkComment('')
        },
    })
    const handleDecide = (assignmentId: string, decisionType: CertificationDecisionType) => {
        const decision = decisions.find((d) => d.assignmentId === assignmentId)
        const comment = buildDefaultComment({
            decisionType,
            accessProfileName: decision?.accessProfile?.displayName,
            riskLevel: decision?.riskLevel,
            scope: 'single',
        })
        decideMutation.mutate({assignmentId, decisionType, comment})
    }

    /* eslint-disable i18next/no-literal-string -- audit trail comments are always English */
    const buildBulkComment = (decisionType: CertificationDecisionType) => {
        const filters: string[] = []
        if (riskLevelFilter) filters.push(`Risk Level: ${riskLevelFilter}`)
        if (decisionTypeFilter) filters.push(`Current Decision: ${decisionTypeFilter}`)
        return buildDefaultComment({
            decisionType,
            scope: 'bulk',
            filterInfo: filters.length > 0 ? filters.join(', ') : 'None',
        })
    }
    /* eslint-enable i18next/no-literal-string */

    const openBulkDialog = () => {
        const defaultType = CertificationDecisionType.KEEP
        setBulkDecisionType(defaultType)
        setBulkComment(buildBulkComment(defaultType))
        bulkDialog.open()
    }

    const decisionLabel = (type: CertificationDecisionType) => {
        switch (type) {
            case CertificationDecisionType.UNDECIDED:
                return i18n.certificationReviews.undecided
            case CertificationDecisionType.KEEP:
                return i18n.certificationReviews.keep
            case CertificationDecisionType.REVOKE:
                return i18n.certificationReviews.decisionRevoke
            case CertificationDecisionType.AUTO_REVOKE:
                return i18n.certificationReviews.decisionAutoRevoke
            default:
                return type
        }
    }

    const groups = groupByRiskLevel(decisions)

    const goBack = useCallback(() => {
        if (locationState?.from === 'campaign' && locationState.campaignId) {
            navigate(routes.accessGovernance.certificationCampaigns.detail(locationState.campaignId))
        } else {
            // Go up two segments: from /:itemId/assignments to the reviews list
            navigate('../..', {relative: 'path', state: {highlightItemId: itemId, campaignId: locationState?.campaignId}})
        }
    }, [navigate, itemId, locationState])

    return (
        <PageLayout
            variant="detail"
            icon={CONCEPT_ICON.assignmentDecision}
            title={identityName ?? i18n.certificationReviews.allAssignments}
            backTo={true}
            loading={isLoading}
            error={error}
            data-testid="assignment-decisions-page"
        >
            {/* Prev / Next navigation */}
            <InlineRow justify="between" className="mb-4">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigation?.previous && navigateToItem(navigation.previous.id)}
                    disabled={!navigation?.previous}
                    data-testid="btn-prev-item"
                >
                    <Icon icon={ChevronLeft} size="xs" className="mr-1"/>
                    {navigation?.previous
                        ? `${i18n.certificationReviews.previousItem}: ${navigation.previous.displayName}`
                        : i18n.certificationReviews.previousItem}
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigation?.next && navigateToItem(navigation.next.id)}
                    disabled={!navigation?.next}
                    data-testid="btn-next-item"
                >
                    {navigation?.next
                        ? `${i18n.certificationReviews.nextItem}: ${navigation.next.displayName}`
                        : i18n.certificationReviews.nextItem}
                    <Icon icon={ChevronRight} size="xs" className="ml-1"/>
                </Button>
            </InlineRow>
            {!readOnly && decideMutation.error && <ErrorBanner error={decideMutation.error}/>}
            {!readOnly && bulkDecideMutation.error && <ErrorBanner error={bulkDecideMutation.error}/>}
            {!readOnly && certifyMutation.error && <ErrorBanner error={certifyMutation.error}/>}

            <Card>
                <CardHeader>
                    <InlineRow justify="between">
                        <CardTitle data-testid="card-title-all-assignments">
                            {identityName
                                ? `${identityName}: ${i18n.certificationReviews.allAssignmentsCount({count: totalItems})}`
                                : i18n.certificationReviews.allAssignmentsCount({count: totalItems})}
                        </CardTitle>
                        <span data-testid="assignment-decisions-count">{totalItems}</span>
                        {!readOnly && (
                            <InlineRow gap="md">
                                <Button
                                    variant="outline"
                                    onClick={openBulkDialog}
                                    data-testid="btn-bulk-decide"
                                >
                                    {i18n.certificationReviews.bulkDecide}
                                </Button>
                                <Button
                                    onClick={certifyDialog.open}
                                    disabled={!allDecided || reviewItem?.status === ReviewItemStatus.COMPLETED}
                                    data-testid="btn-certify"
                                >
                                    <Icon icon={ShieldCheck} className="mr-2"/>
                                    {i18n.certificationReviews.certify}
                                </Button>
                            </InlineRow>
                        )}
                    </InlineRow>
                    {/* Filters row */}
                    <InlineRow gap="md" className="mt-4">
                        <UnifiedSearchBar
                            contract={CertificationReviewControllerDecisionsEndpoint}
                            value={searchAndFilters}
                            onChange={setSearchAndFilters}
                            placeholder={i18n.certificationReviews.searchAssignments as string}
                            testIdNamespace="assignment-decisions"
                        />
                    </InlineRow>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead data-testid="col-access-profile">{i18n.certificationReviews.accessProfile}</TableHead>
                                <TableHead className="w-0" data-testid="col-decision">{i18n.certificationReviews.filterByDecision}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {groups.map((group) => (
                                <Fragment key={`group-${group.riskLevel ?? 'none'}`}>
                                    {/* Risk level section header */}
                                    <TableRow className="bg-muted/50 hover:bg-muted/50">
                                        <TableCell colSpan={2} className="py-2">
                                            <InlineRow gap="md">
                                                <RiskLevelBadge riskLevel={group.riskLevel}/>
                                                <MutedText size="sm" weight="medium">({group.decisions.length})</MutedText>
                                            </InlineRow>
                                        </TableCell>
                                    </TableRow>
                                    {/* Decisions in this group */}
                                    {group.decisions.map((decision) => (
                                        <TableRow
                                            key={decision.id}
                                            className={decision.decisionType === CertificationDecisionType.AUTO_REVOKE ? 'opacity-50' : undefined}
                                            data-testid={`decision-row-${decision.id}`}
                                        >
                                            <TableCell className="font-medium" data-testid={`decision-access-profile-${decision.id}`}>
                                                {decision.accessProfile?.displayName ?? '\u2014'}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <DecisionToggle
                                                    decision={decision}
                                                    onDecide={handleDecide}
                                                    isPending={decideMutation.isPending}
                                                    readOnly={readOnly}
                                                    i18n={i18n}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </Fragment>
                            ))}
                            {decisions.length === 0 && (
                                <TableEmptyState
                                    colSpan={2}
                                    hasFilters={hasActiveFilters}
                                    matchMessage={i18n.certificationReviews.noReviewsFound}
                                    emptyMessage={i18n.certificationReviews.noReviewsFound}
                                />
                            )}
                        </TableBody>
                    </Table>
                    {totalPages > 0 && (
                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            totalItems={totalItems}
                            totalPages={totalPages}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                            pageSizeOptions={pageSizeOptions}
                        />
                    )}
                </CardContent>
            </Card>

            {/* Bulk Decision Dialog */}
            {!readOnly && <Dialog open={bulkDialog.isOpen} onOpenChange={(open) => !open && bulkDialog.close()}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.certificationReviews.bulkDecideTitle}</DialogTitle>
                        <DialogDescription>
                            {i18n.certificationReviews.bulkDecideDescription}
                        </DialogDescription>
                    </DialogHeader>

                    {bulkDecideMutation.error && <ErrorBanner error={bulkDecideMutation.error}/>}
                    <FormGrid className="py-4">
                        {/* Filter summary */}
                        <MutedText size="sm">
                            {i18n.certificationReviews.bulkDecideAffected({count: totalItems})}
                            {riskLevelFilter && (
                                // eslint-disable-next-line i18next/no-literal-string
                                <span> &middot; {i18n.certificationReviews.filterByRiskLevel}: {riskLevelFilter}</span>
                            )}
                            {decisionTypeFilter && (
                                // eslint-disable-next-line i18next/no-literal-string
                                <span> &middot; {i18n.certificationReviews.filterByDecision}: {decisionLabel(decisionTypeFilter as CertificationDecisionType)}</span>
                            )}
                        </MutedText>

                        {/* Decision type selection */}
                        <FormField label={i18n.certificationReviews.filterByDecision} htmlFor="bulk-decision-type">
                            <SegmentedToggle<CertificationDecisionType>
                                size="md"
                                value={bulkDecisionType}
                                onChange={(v) => {
                                    setBulkDecisionType(v)
                                    setBulkComment(buildBulkComment(v))
                                }}
                                /* eslint-disable i18next/no-literal-string -- technical test-id identifiers and tone keys, not user-visible strings */
                                options={[
                                    {value: CertificationDecisionType.KEEP, label: i18n.certificationReviews.keep, tone: 'success', testId: 'bulk-btn-keep'},
                                    {value: CertificationDecisionType.REVOKE, label: i18n.certificationReviews.decisionRevoke, tone: 'destructive', testId: 'bulk-btn-revoke'},
                                    {value: CertificationDecisionType.UNDECIDED, label: i18n.certificationReviews.undecided, tone: 'neutral', testId: 'bulk-btn-undecided'},
                                ]}
                                /* eslint-enable i18next/no-literal-string */
                            />
                        </FormField>
                        {/* Comment */}
                        <FormField label={i18n.certificationReviews.comment} htmlFor="input-bulk-comment">
                            <Textarea
                                id="input-bulk-comment"
                                value={bulkComment}
                                onChange={(e) => setBulkComment(e.target.value)}
                                rows={8}
                                className="font-mono text-xs"
                                data-testid="input-bulk-comment"
                            />
                        </FormField>

                        {/* Summary */}
                        <Strong>
                            {i18n.certificationReviews.bulkDecideSummary({
                                count: totalItems,
                                decision: decisionLabel(bulkDecisionType),
                            })}
                        </Strong>
                    </FormGrid>

                    <SaveCancelFooter
                        testIdPrefix="bulk"
                        onCancel={bulkDialog.close}
                        saving={bulkDecideMutation.isPending}
                        saveLabel={i18n.common.confirm}
                        destructive={bulkDecisionType === CertificationDecisionType.REVOKE}
                        saveType="button"
                        onSave={() => bulkDecideMutation.mutate()}
                    />
                </DialogContent>
            </Dialog>}

            {/* Certify Confirmation Dialog */}
            {!readOnly && <Dialog open={certifyDialog.isOpen} onOpenChange={certifyDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.certificationReviews.certify}</DialogTitle>
                        <DialogDescription>
                            {i18n.certificationReviews.certifyConfirmDescription({name: identityName ?? ''})}
                        </DialogDescription>
                    </DialogHeader>
                    {certifyMutation.error && <ErrorBanner error={certifyMutation.error}/>}
                    <SaveCancelFooter
                        testIdPrefix="certify"
                        onCancel={certifyDialog.close}
                        saving={certifyMutation.isPending}
                        saveLabel={i18n.certificationReviews.certify}
                        saveType="button"
                        onSave={() => certifyMutation.mutate()}
                    />
                </DialogContent>
            </Dialog>}
        </PageLayout>
    )
}
